import { ProductConfiguration, EOfferProductData } from '../../shared/models/product';

import { FormOffer } from './form-offer';
import { Offer, ProductFamily, ProductTable, TemplateDescription } from './offer';
import { offerEquals, offerCopy } from './offer-utils';

export enum WizardStep {
	HEADER = 0, CART = 1, QUANTITY = 2, REVIEW = 3
}

export class AppWizardStateOffer {
	static COMPUTE_INDEX_STEP(offer: Offer | FormOffer) {
		const form = offer instanceof FormOffer ? offer : new FormOffer().setOffer(offer);
		if (!form.isOfferHeaderValid()) {
			return WizardStep.HEADER;
		}
		if (form.getFormProducts().length === 0) {
			return WizardStep.HEADER;
		}
		if (!form.getFormProducts().isValid()) {
			return WizardStep.CART;
		}
		return WizardStep.REVIEW;
	}

	readonly form = new FormOffer();
	private lastSavedOffer: Offer;

	constructor(offer: Offer, lastSavedOffer: Offer) {
		this.form = new FormOffer();
		this.form.setOffer(offer);
		this.lastSavedOffer = lastSavedOffer;
	}

	getOfferType() {
		return this.form.getOfferType();
	}

	extractOffer() {
		return this.form.getOffer();
	}

	isDifferenceWithLastSaved() {
		return !offerEquals(this.extractOffer(), this.lastSavedOffer);
	}

	updateLastSavedOffer(offer: Offer) {
		this.form.setOffer(offer);
		this.lastSavedOffer = offerCopy(this.extractOffer());
	}

	reset() {
		this.form.setOffer(this.lastSavedOffer);
		this.form.updateValueAndValidity();
	}

	computeIndexStep() {
		return AppWizardStateOffer.COMPUTE_INDEX_STEP(this.form);
	}
}

class AppWizardStateUi {
	adycoFields?: string[];
	defaultFields?: string[];
	/**
	 * by soldTo shipTo, endUser
	 */
	private readonly productFamilies: Record<string, ProductFamily[]> = {};
	private newTemplateProduct = true;
	private readonly templateNumberDetails = new Map<number, TemplateDescription>();
	private readonly productTables: Record<string, ProductTable | undefined> = {};
	private readonly productTablePeriods: Record<string, number | undefined> = {};
	private readonly characteristicNamesByFamily: Record<string, string[]> = {};
	private lastSelectedProductFamily: ProductFamily | undefined;
	private lastSelectedSoldTo: string | undefined;
	private readonly configurationData = new Map<string, ProductConfiguration>();
	private readonly productData = new Map<string, EOfferProductData>();

	isNewTemplateProduct() {
		return this.newTemplateProduct;
	}

	setNewTemplateProduct(newTemplateProduct: boolean) {
		this.newTemplateProduct = newTemplateProduct;
	}

	getProductFamilies(soldTo: string) {
		const code = soldTo;
		if (code in this.productFamilies) {
			return this.productFamilies[code];
		}
		return undefined;
	}

	setProductFamilies(soldTo: string, productFamilies: ProductFamily[]) {
		this.productFamilies[soldTo] = productFamilies;
	}

	setSoldTo(soldTo: string) {
		this.lastSelectedSoldTo = soldTo;
	}

	getLastSelectedSoldTo() {
		return this.lastSelectedSoldTo;
	}

	setProductTablePeriod(
		productFamily: string,
		soldTo: string,
		shipTo: string,
		endUser: string,
		period: number
	) {
		this.productTablePeriods[this.createProductTableId(productFamily, soldTo, shipTo, endUser)] = period;
	}

	getProductTablePeriod(productFamily: string, soldTo: string, shipTo: string, endUser: string) {
		const productId = this.createProductTableId(productFamily, soldTo, shipTo, endUser);
		if (productId in this.productTables) {
			return this.productTablePeriods[productId];
		}
		return undefined;
	}

	findAvailableProductForUfoProduct(
		ufoProductRef: number
	) {
		// search in all product tables if a product (of TYPE PRODUCT and not TEMPLATE) exists with the same reference
		for (const productTableCode of Object.getOwnPropertyNames(this.productTables)) {
			const productTable = this.productTables[productTableCode];
			if (!productTable) {
				continue;
			}
			const productFound = productTable.products.find(
				product =>
					product.configurations?.some(conf => conf.ufoProductRef === ufoProductRef)
			);
			if (productFound) {
				return productFound;
			}
		}
		return undefined;
	}

	setCharacteristicNames(familyCode: string, characteristicNames: string[]) {
		this.characteristicNamesByFamily[familyCode] = characteristicNames;
	}

	getCharacteristicNames(familyCode: string) {
		if (familyCode in this.characteristicNamesByFamily) {
			return this.characteristicNamesByFamily[familyCode];
		}
		return undefined;
	}

	getSelectedProductFamily() {
		return this.lastSelectedProductFamily;
	}

	setSelectedProductFamily(productFamily: ProductFamily | undefined) {
		this.lastSelectedProductFamily = productFamily;
	}

	getSelectedProductFamilyDescription() {
		return this.lastSelectedProductFamily ? this.lastSelectedProductFamily.subCategoryDescription : '';
	}

	hasTemplateDetail(templateNumber: number) {
		return this.templateNumberDetails.has(templateNumber);
	}

	updateTemplateNumberDetails(templateNumber: number, templateDescription: TemplateDescription) {
		this.templateNumberDetails.set(templateNumber, templateDescription);
	}

	getTemplateNumberDetails(templateNumber: number) {
		return this.templateNumberDetails.get(templateNumber);
	}

	updateConfigurationData(configurationNumber: string, product: ProductConfiguration) {
		this.configurationData.set(configurationNumber, product);
	}

	getConfigurationData(configurationNumber: string) {
		return this.configurationData.get(configurationNumber);
	}

	hasConfigurationData(configurationNumber: string) {
		return this.configurationData.has(configurationNumber);
	}

	updateSimulationData(uniqueRef: string, productConfiguration: ProductConfiguration) {
		const productData: EOfferProductData = { ...productConfiguration };
		productData.uniqueRef = uniqueRef;
		this.productData.set(uniqueRef, productData);
	}

	removeSimulationData(uniqueRef: string) {
		this.productData.delete(uniqueRef);
	}

	getSimulationData(uniqueRef: string) {
		return this.productData.get(uniqueRef);
	}

	hasSimulationData(uniqueRef: string) {
		return this.productData.has(uniqueRef);
	}

	private createProductTableId(productFamily: string, soldTo: string, shipTo: string, endUser: string) {
		return `${ productFamily }-${ soldTo }-${ shipTo }-${ endUser }`;
	}
}

export class AppWizardState {
	/**
	 * state remains valid during the wizard session
	 * wizard session starts when following actions are made:
	 * - create new offer
	 * - create a copy of an existing accepted offer
	 */
	stepIndex: WizardStep = WizardStep.HEADER;
	isValid = false;
	exiting = false;
	errorInPage = false;
	readonly scrollPositionByStep = [0, 0, 0, 0, 0];
	readonly offer: AppWizardStateOffer;
	readonly ui: AppWizardStateUi;
	constructor(offer: Offer, lastSavedOffer: Offer) {
		this.offer = new AppWizardStateOffer(offer, lastSavedOffer);
		this.ui = new AppWizardStateUi();
	}
}
